import { useState } from 'react';
import clsx from 'clsx';
import { collapseMarketErrors, failureCause } from './syncError';

/**
 * A failure as one plain line ("Database query failed"), with "Show details"
 * to unfold the raw text when the line had to leave something out. Used
 * wherever an error is displayed, so every page words the same error the same way.
 */
export function ErrorCause({
  raw,
  cause,
  suffix,
  className,
}: {
  /** The raw error, shown behind "Show details". */
  raw: string | null | undefined;
  /** The text to read the cause from, when it differs from `raw` (an API-derived reason). */
  cause?: string | null;
  /** Trailing context on the same line: " · since 9 Oct 07:00". */
  suffix?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const line = failureCause(cause ?? raw);
  const details = collapseMarketErrors((raw ?? '').trim());
  const hasMore = details.length > 0 && details !== line;
  return (
    <div className={clsx('min-w-0', className)}>
      <p className="flex min-w-0 flex-wrap items-baseline gap-x-1 text-body-xs">
        <span className="min-w-0 break-words [overflow-wrap:anywhere]">{line}</span>
        {suffix && <span className="text-text-tertiary">{suffix}</span>}
        {hasMore && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
            aria-expanded={open}
            className="text-text-tertiary hover:text-text-primary hover:underline"
          >
            · {open ? 'Hide details' : 'Show details'}
          </button>
        )}
      </p>
      {open && (
        <p className="mt-1 whitespace-pre-wrap break-words text-body-xs leading-relaxed text-text-secondary [overflow-wrap:anywhere]">
          {details}
        </p>
      )}
    </div>
  );
}
